/**
 * `/app/friend-trades` — сделки Ильи на графиках Pulsar (PULSE-404).
 *
 * Слева — последние N сделок (таблица friend_trips: трипы из выгрузок его исполнений, сверены с closedPnl Bybit).
 * Справа — тот же MarketChart, что на странице «Рынок» (цена/объём, OI, Net Delta, тейкеры, L/S, ликвидации,
 * фандинг), в историческом окне вокруг сделки: вход (первое исполнение), доборы, частичные закрытия, выход.
 * Цель — визуально искать общее в моменте входа. Admin-only.
 *
 * «Только до входа» — график обрезан по началу минуты входа (бары строго до неё, будущего не видно).
 * `?cmp=SYM@unix,…` — похожие моменты, где он НЕ входил (поиск lookalike_oct.py), отдельными строками над списком;
 * `?trade=<id>` — выбрать сделку, `?pre=1` — сразу режим «только до входа».
 */
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MarketChart, ChartViewToggle, useChartView, CG_VIEW_OMIT, type TradeMarkers } from '@/components/market/MarketChart'
import { useFriendTrades, useMarketSeries, type FriendTrade, type Timeframe } from '@/hooks/useMarketApi'
import { cn } from '@/lib/cn'

const TFS: Timeframe[] = ['1m', '5m', '15m']
const TF_SEC: Record<string, number> = { '1m': 60, '5m': 300, '15m': 900 }
// h=0 — режим «только вход»: окно [вход − 45 мин; вход + 10 мин] (PULSE-404, обрезка хвостов)
const BEFORE = [{ h: 0, label: '−45/+10 мин' }, { h: 6, label: '6ч до' }, { h: 24, label: '24ч до' }, { h: 72, label: '3д до' }]
const MAX_LIMIT = 1500

const fmtT = (t: number) => new Date(t * 1000).toLocaleString('ru-RU', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
})
const hold = (a: number, b: number) => {
  const m = Math.round((b - a) / 60)
  return m < 60 ? `${m}м` : m < 1440 ? `${(m / 60).toFixed(1)}ч` : `${(m / 1440).toFixed(1)}д`
}
const px = (v: number) => Number(v.toPrecision(5)).toString()

type Moment = { symbol: string; t: number }

export default function FriendTrades() {
  const [params] = useSearchParams()
  const moments: Moment[] = useMemo(() => (params.get('cmp') ?? '').split(',').map((x) => x.split('@'))
    .filter((x) => x.length === 2 && /^[A-Z0-9]+$/.test(x[0]) && Number(x[1]) > 0)
    .map(([symbol, t]) => ({ symbol, t: Number(t) })), [params])
  const trades = useFriendTrades(60)
  const [sel, setSel] = useState<number | null>(params.get('trade') ? Number(params.get('trade')) : null)
  const [selM, setSelM] = useState<number | null>(null)   // индекс в moments — выбран момент без входа
  const [tf, setTf] = useState<Timeframe>(params.get('pre') ? '1m' : '5m')
  const [before, setBefore] = useState(params.get('pre') ? 0 : 24)
  const [pre, setPre] = useState(!!params.get('pre'))
  const [view, setView] = useChartView()

  const list = trades.data?.trades ?? []
  useEffect(() => { if (sel == null && selM == null && list.length) setSel(list[0].id) }, [list, sel, selM])
  const mo: Moment | undefined = selM != null ? moments[selM] : undefined
  const tr: FriendTrade | undefined = mo ? undefined : list.find((x) => x.id === sel)
  const cur = mo ? { symbol: mo.symbol, entry_t: mo.t, exit_t: mo.t } : tr

  // Окно: [вход − before; max(выход, вход + 2ч) + 1ч], ограничено MAX_LIMIT баров; h=0 — [вход − 45 мин; вход + 10 мин].
  // «Только до входа»: конец окна = начало минуты входа.
  const { anchor, limit } = useMemo(() => {
    if (!cur) return { anchor: null as number | null, limit: 300 }
    const s = TF_SEC[tf]
    const end = pre ? Math.floor(cur.entry_t / 60) * 60
      : before === 0 ? cur.entry_t + 10 * 60 : Math.max(cur.exit_t, cur.entry_t + 2 * 3600) + 3600
    const start = before === 0 ? cur.entry_t - 45 * 60 : cur.entry_t - before * 3600
    return { anchor: end, limit: Math.min(MAX_LIMIT, Math.max(10, Math.ceil((end - start) / s))) }
  }, [cur?.symbol, cur?.entry_t, cur?.exit_t, tf, before, pre])
  const series = useMarketSeries(cur?.symbol ?? '', tf, limit, anchor)

  const markers: TradeMarkers | undefined = useMemo(() => {
    if (pre) return undefined
    if (mo) {
      const c = series.data?.candles.find((x) => x.t === Math.floor(mo.t / TF_SEC[tf]) * TF_SEC[tf])
      return c ? { direction: 'short', signal: { t: mo.t, price: c.c, text: 'похожий момент, входа нет' } } : undefined
    }
    if (!tr) return undefined
    const opens = tr.fills.filter((f) => f.kind === 'open')
    const closes = tr.fills.filter((f) => f.kind === 'close')
    const extra: NonNullable<TradeMarkers['extra']> = []
    opens.slice(1).forEach((f, i) => extra.push({ t: f.t, price: f.px, kind: 'add', text: `добор ${i + 1} ${px(f.px)}` }))
    if (closes.length > 1) closes.slice(0, -1).forEach((f) => extra.push({ t: f.t, price: f.px, kind: 'partial', text: `частично ${px(f.px)}` }))
    // группируем исполнения одного ордера/секунды — не плодим метки
    const seen = new Set<string>(); const dedup = extra.filter((x) => { const k = `${x.kind}${Math.round(x.t / 60)}`; if (seen.has(k)) return false; seen.add(k); return true })
    return {
      direction: tr.side,
      entry: { t: tr.entry_t, price: tr.first_px },
      exit: { t: tr.exit_t, price: Number(tr.exit_avg.toPrecision(6)) },
      extra: dedup,
    }
  }, [tr, mo, pre, series.data, tf])

  return (
    <div className="p-4 sm:p-6 max-w-[1700px]">
      <div className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-textFaint uppercase tracking-wider mb-1">Сделки Ильи</div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {mo ? <>{mo.symbol} <span className="text-base font-mono text-textDim">{pre ? 'момент' : 'похожий момент, входа нет'} · {fmtT(mo.t)} UTC</span></>
              : tr ? <>{tr.symbol} <span className={cn('text-base font-mono', tr.side === 'short' ? 'text-rose' : 'text-pulse')}>{tr.side}</span></> : 'Последние 30 сделок'}
            {tr && !pre && (
              <span className={cn('ml-3 text-base font-mono', tr.ret_pct >= 0 ? 'text-pulse' : 'text-rose')}>
                {tr.ret_pct >= 0 ? '+' : ''}{tr.ret_pct.toFixed(2)}% · {tr.pnl_usd >= 0 ? '+' : ''}${tr.pnl_usd.toFixed(2)}
              </span>
            )}
          </h1>
          <div className="text-xs text-textDim mt-1">
            Время — UTC. Вход = первое исполнение (голубая стрелка), доборы — оранжевые, выход — средняя цена закрытия.
            {trades.data?.built_at && <> Данные выгрузки на {new Date(trades.data.built_at).toLocaleString('ru-RU')}.</>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setPre(!pre)} title="Бары строго до минуты входа — как он видел график в момент решения"
            className={cn('px-3 py-1.5 text-xs border border-line rounded', pre ? 'bg-fire-faint text-fire' : 'text-textDim hover:text-text hover:bg-elevated')}>только до входа</button>
          <ChartViewToggle view={view} onChange={setView} />
          <div className="flex border border-line rounded overflow-hidden">
            {TFS.map((f) => (
              <button key={f} onClick={() => setTf(f)}
                className={cn('px-3 py-1.5 text-xs font-mono', tf === f ? 'bg-fire-faint text-fire' : 'text-textDim hover:text-text hover:bg-elevated')}>{f}</button>
            ))}
          </div>
          <div className="flex border border-line rounded overflow-hidden">
            {BEFORE.map((b) => (
              <button key={b.h} onClick={() => { setBefore(b.h); if (b.h === 0) setTf('1m') }}
                className={cn('px-3 py-1.5 text-xs', before === b.h ? 'bg-fire-faint text-fire' : 'text-textDim hover:text-text hover:bg-elevated')}>{b.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-3 flex-col lg:flex-row">
        <div className="lg:w-[330px] shrink-0 border border-line rounded bg-surface overflow-auto" style={{ maxHeight: 780 }}>
          {trades.isLoading && <div className="p-3 text-sm text-textDim">Загрузка…</div>}
          {trades.isError && <div className="p-3 text-sm text-rose">Не удалось загрузить сделки.</div>}
          <table className="w-full text-xs">
            <thead className="text-textFaint sticky top-0 bg-surface">
              <tr><th className="text-left p-2">вход (UTC)</th><th className="text-left">монета</th><th className="text-right">итог</th><th className="text-right pr-2">держал</th></tr>
            </thead>
            <tbody>
              {moments.map((m, i) => (
                <tr key={`m${i}`} onClick={() => setSelM(i)}
                  className={cn('cursor-pointer border-t border-line', selM === i ? 'bg-fire-faint' : 'hover:bg-elevated')}>
                  <td className="p-2 font-mono whitespace-nowrap">{fmtT(m.t)}</td>
                  <td className="font-mono">{m.symbol.replace('USDT', '')}</td>
                  <td colSpan={2} className="text-right pr-2 text-textDim">{pre ? 'момент' : 'похожий, без входа'}</td>
                </tr>
              ))}
              {list.map((x) => (
                <tr key={x.id} onClick={() => { setSel(x.id); setSelM(null) }}
                  className={cn('cursor-pointer border-t border-line', selM == null && sel === x.id ? 'bg-fire-faint' : 'hover:bg-elevated')}>
                  <td className="p-2 font-mono whitespace-nowrap">{fmtT(x.entry_t)}</td>
                  <td className="font-mono">
                    {x.symbol.replace('USDT', '')}
                    {x.side === 'long' && <span className="ml-1 text-pulse">L</span>}
                    {x.n_open > 1 && <span className="ml-1 text-fire" title="доборы">+{x.n_open - 1}</span>}
                    {x.n_close > 1 && <span className="ml-1 text-textDim" title="частичные закрытия">÷{x.n_close}</span>}
                  </td>
                  <td className={cn('text-right font-mono', pre ? 'text-textFaint' : x.ret_pct >= 0 ? 'text-pulse' : 'text-rose')}>{pre ? '—' : `${x.ret_pct >= 0 ? '+' : ''}${x.ret_pct.toFixed(1)}%`}</td>
                  <td className="text-right pr-2 font-mono text-textDim">{pre ? '' : hold(x.entry_t, x.exit_t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex-1 border border-line rounded bg-surface relative" style={{ height: 780 }}>
          {series.isLoading && <div className="absolute inset-0 grid place-items-center text-textDim text-sm">Загрузка графика…</div>}
          {series.isError && <div className="absolute inset-0 grid place-items-center text-rose text-sm">Не удалось загрузить график.</div>}
          {series.data && series.data.candles.length === 0 && (
            <div className="absolute inset-0 grid place-items-center text-textDim text-sm">Нет данных по {cur?.symbol} / {tf} за это время.</div>
          )}
          {series.data && series.data.candles.length > 0 && <MarketChart key={view} data={series.data} markers={markers} omit={view === 'cg' ? CG_VIEW_OMIT : []} />}
        </div>
      </div>
    </div>
  )
}
